import {
  Dialog,
  Box,
  IconButton,
  CloseIcon,
  DialogContent,
  DialogContentText,
  Stack,
  useTheme,
  Alert
} from "@carrotfertility/carotene-core"
import React from "react"
import { FormattedMessage, useIntl, defineMessage } from "react-intl"
import { SupportedLocale, getSupportedLocaleDisplayName } from "../app/IntlMessages"

const SUPPORTED_LOCATION_TEXT = {
  [SupportedLocale.Spanish_UnitedStates]: defineMessage({ defaultMessage: "the US, Mexico, or Argentina" }),
  [SupportedLocale.French_Canada]: defineMessage({ defaultMessage: "Canada or Switzerland" }),
  [SupportedLocale.German_Germany]: defineMessage({ defaultMessage: "Germany or Switzerland" }),
  [SupportedLocale.Portuguese_Brazil]: defineMessage({ defaultMessage: "Brazil" }),
  [SupportedLocale.Hindi_India]: defineMessage({ defaultMessage: "India" })
}

const NonEnglishPreferredLocaleDialogText = ({
  preferredLocale
}: {
  preferredLocale: Exclude<SupportedLocale, SupportedLocale.English_UnitedStates>
}): JSX.Element => {
  const intl = useIntl()
  const theme = useTheme()
  const supportedLocation = intl.formatMessage(SUPPORTED_LOCATION_TEXT[preferredLocale])

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Stack>
        <FormattedMessage
          defaultMessage="You've changed your language to {selectedLanguage}."
          values={{ selectedLanguage: getSupportedLocaleDisplayName(preferredLocale) }}
          description="selectedLanguage options are 'Spanish', 'French', 'German', 'Portuguese', and 'Hindi'"
        />
      </Stack>
      <Stack>
        <FormattedMessage
          defaultMessage="If you are employed in {supportedLocation}, your Carrot app and communications will be in {selectedLanguage}. You can update preferred language in your account settings."
          values={{
            supportedLocation,
            selectedLanguage: getSupportedLocaleDisplayName(preferredLocale)
          }}
          description="supportedLocation is a country name; selectedLanguage options are 'Spanish', 'French', 'German', 'Portuguese', and 'Hindi'"
        />
      </Stack>
      <Stack>
        <Alert>
          <FormattedMessage
            defaultMessage="If you aren't employed in {supportedLocation}, your experience will return to U.S. English once your account is created."
            values={{
              supportedLocation
            }}
          />
        </Alert>
      </Stack>
    </Stack>
  )
}

export const PreferredLocaleChangedDialog = ({
  preferredLocale,
  modalOpen,
  setModalOpen
}: {
  preferredLocale: SupportedLocale
  modalOpen: boolean
  setModalOpen: React.Dispatch<boolean>
}): JSX.Element => {
  const intl = useIntl()

  function handleModalClose() {
    setModalOpen(false)
  }

  return (
    <Dialog aria-describedby="language-selection-info-modal-description" open={modalOpen} maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end"
        }}
      >
        <IconButton aria-label={intl.formatMessage({ defaultMessage: "close" })} onClick={handleModalClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        <DialogContentText id="language-selection-info-modal-description">
          {preferredLocale === SupportedLocale.English_UnitedStates ? (
            <FormattedMessage defaultMessage="You've changed your language to U.S. English. Your Carrot app and communications will be in U.S. English. You can update preferred language in your account settings." />
          ) : (
            <NonEnglishPreferredLocaleDialogText preferredLocale={preferredLocale} />
          )}
        </DialogContentText>
      </DialogContent>
    </Dialog>
  )
}
